import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col, Tabs, Tab } from 'react-bootstrap';
import moment from 'moment';

// Funciones de utilidad para formatear números con comas de miles/millones
const formatWithCommas = (value) => {
    if (value === null || value === undefined || value === '') return '';
    // Obtener sólo los caracteres numéricos y el punto decimal
    const strValue = String(value).replace(/[^0-9.]/g, '');
    const parts = strValue.split('.');
    
    // Formatear la parte entera con comas
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    // Unir parte entera y decimales (limitado a 2 decimales)
    if (parts[1] !== undefined) {
        return `${parts[0]}.${parts[1].slice(0, 2)}`;
    }
    return parts[0];
};

const getCardBrand = (pan) => {
    const cleanPan = String(pan || '').replace(/\D/g, '');
    if (cleanPan.startsWith('4')) {
        return 'visa';
    } else if (/^(5[1-5]|2[2-7])/.test(cleanPan)) {
        return 'mastercard';
    } else if (/^(3[47])/.test(cleanPan)) {
        return 'amex';
    }
    return 'unknown';
};

export const CreateRecurringModal = ({ show, onHide, onSubmit, loading }) => {
    const [activeTab, setActiveTab] = useState('cliente');
    const INITIAL_FORM_STATE = {
        // Información del Cliente
        firstName: '',
        lastName: '',
        email: '',
        address1: '',
        city: '',
        country: 'MX',
        
        // Datos de la Tarjeta
        pan: '',
        expMonth: '',
        expYear: '',
        holderName: '',
        cvv: '',
        
        // Configuración del Plan
        concept: '',
        amount: '',
        currency: '484', 
        chargeDay: '1',
        chargeNumber: '1',
        startDate: new Date().toISOString().split('T')[0],
        periodicity: 'monthly',
        initialCharge: false,
        initialAmount: '0'
    };

    const [formData, setFormData] = useState(INITIAL_FORM_STATE);

    const renderBrandBadge = () => {
        const brand = getCardBrand(formData.pan);
        if (brand === 'visa') {
            return <span className="badge bg-primary ms-2" style={{ textTransform: 'uppercase' }}>Visa</span>;
        }
        if (brand === 'mastercard') {
            return <span className="badge bg-warning text-dark ms-2" style={{ textTransform: 'uppercase' }}>Mastercard</span>;
        }
        if (brand === 'amex') {
            return <span className="badge bg-info text-dark ms-2" style={{ textTransform: 'uppercase' }}>Amex</span>;
        }
        return null;
    };

    const resetForm = () => {
        setFormData({ ...INITIAL_FORM_STATE, startDate: new Date().toISOString().split('T')[0] });
        setActiveTab('cliente');
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'pan') {
            const cleanValue = value.replace(/\D/g, '');
            const brand = getCardBrand(cleanValue);
            const maxLength = brand === 'amex' ? 15 : 16;
            const newPan = cleanValue.slice(0, maxLength);
            
            setFormData(prev => {
                const currentBrand = getCardBrand(newPan);
                const maxCvvLength = currentBrand === 'amex' ? 4 : 3;
                return {
                    ...prev,
                    pan: newPan,
                    cvv: prev.cvv.slice(0, maxCvvLength)
                };
            });
            return;
        }

        if (name === 'cvv') {
            const cleanValue = value.replace(/\D/g, '');
            const brand = getCardBrand(formData.pan);
            const maxLength = brand === 'amex' ? 4 : 3;
            setFormData(prev => ({
                ...prev,
                cvv: cleanValue.slice(0, maxLength)
            }));
            return;
        }

        if (name === 'amount') {
            // Eliminar comas para guardar el número puro en el estado
            const rawValue = value.replace(/,/g, '');
            // Permitir solo números y un punto decimal (máximo 2 decimales)
            if (rawValue === '' || /^\d*\.?\d{0,2}$/.test(rawValue)) {
                setFormData(prev => ({
                    ...prev,
                    [name]: rawValue
                }));
            }
            return;
        }

        if (name === 'initialAmount') {
            const rawValue = value.replace(/,/g, '');
            if (rawValue === '' || /^\d*\.?\d{0,2}$/.test(rawValue)) {
                setFormData(prev => ({
                    ...prev,
                    [name]: rawValue
                }));
            }
            return;
        }

        if (name === 'periodicity') {
            setFormData(prev => ({
                ...prev,
                periodicity: value,
                chargeDay: value === 'monthly' ? '' : '1'
            }));
            return;
        }

        if (name === 'initialCharge') {
            setFormData(prev => ({
                ...prev,
                initialCharge: checked,
                initialAmount: checked ? (prev.initialAmount === '0' ? '' : prev.initialAmount) : '0',
                cvv: checked ? prev.cvv : ''
            }));
            return;
        }

        setFormData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    };

    // Validaciones por paso
    const isClienteValid = () => {
        return (
            formData.firstName.trim() !== '' &&
            formData.lastName.trim() !== '' &&
            formData.email.trim() !== '' &&
            formData.address1.trim() !== '' &&
            formData.city.trim() !== ''
        );
    };

    const isTarjetaValid = () => {
        const brand = getCardBrand(formData.pan);
        const expectedPanLength = brand === 'amex' ? 15 : 16;
        const expectedCvvLength = brand === 'amex' ? 4 : 3;

        const isCvvValid = formData.initialCharge
            ? (formData.cvv.trim().length === expectedCvvLength)
            : true;

        return (
            formData.holderName.trim() !== '' &&
            formData.pan.length === expectedPanLength &&
            formData.expMonth.length === 2 &&
            formData.expYear.length === 4 &&
            isCvvValid
        );
    };

    const isPlanValid = () => {
        const brand = getCardBrand(formData.pan);
        const expectedCvvLength = brand === 'amex' ? 4 : 3;

        const isChargeDayValid = formData.periodicity === 'monthly'
            ? (formData.chargeDay !== '' && parseInt(formData.chargeDay) >= 1 && parseInt(formData.chargeDay) <= 31)
            : true;

        const isCvvSuppliedIfInitialCharge = formData.initialCharge
            ? (formData.cvv.trim().length === expectedCvvLength)
            : true;

        const isInitialAmountValid = formData.initialCharge
            ? (parseFloat(formData.initialAmount) > 0)
            : true;

        return (
            formData.concept.trim() !== '' &&
            parseFloat(formData.amount) > 0 &&
            formData.chargeNumber >= 1 &&
            isChargeDayValid &&
            formData.startDate !== '' &&
            isCvvSuppliedIfInitialCharge &&
            isInitialAmountValid
        );
    };

    const getRecurringChargeAmount = () => {
        const amount = parseFloat(formData.amount) || 0;
        const chargeNumber = parseInt(formData.chargeNumber) || 1;
        return (amount / chargeNumber).toFixed(2);
    };

    const generatePaymentProjection = () => {
        const chargeNumber = parseInt(formData.chargeNumber) || 1;
        const periodicity = formData.periodicity || 'monthly';
        const chargeDay = parseInt(formData.chargeDay) || 1;
        const startDateStr = formData.startDate;

        const projection = [];
        const baseDate = moment(startDateStr || new Date());
        const amountPerCharge = getRecurringChargeAmount();

        for (let i = 0; i < chargeNumber; i++) {
            let paymentDate = moment(baseDate);

            if (periodicity === 'monthly') {
                if (i > 0) {
                    paymentDate.add(i, 'months');
                    const targetDay = chargeDay;
                    const daysInMonth = paymentDate.daysInMonth();
                    paymentDate.date(Math.min(targetDay, daysInMonth));
                }
            } else if (periodicity === 'weekly') {
                paymentDate.add(i * 7, 'days');
            } else if (periodicity === 'biweekly') {
                paymentDate.add(i * 15, 'days');
            }

            projection.push({
                index: i + 1,
                date: paymentDate.format('YYYY-MM-DD'),
                amount: amountPerCharge
            });
        }
        return projection;
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (isClienteValid() && isTarjetaValid() && isPlanValid()) {
            try {
                const res = await onSubmit(formData);
                if (res && res.success !== false) {
                    resetForm();
                }
            } catch (err) {
                console.error("Error al enviar el formulario:", err);
            }
        }
    };

    const handleHide = () => {
        resetForm();
        onHide();
    };

    return (
        <Modal show={show} onHide={handleHide} centered size="lg">
            <Modal.Header closeButton style={{ backgroundColor: '#F8F9FA' }}>
                <Modal.Title className="fw-bold" style={{ color: '#3E3E3E' }}>
                    <i className="bi bi-calendar-plus me-2"></i> Nuevo Plan Recurrente
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleFormSubmit}>
                <Modal.Body className="p-4">
                    <Tabs
                        activeKey={activeTab}
                        onSelect={(k) => {
                            // Impedir acceso a pasos futuros si el actual no es válido
                            if (k === 'tarjeta' && !isClienteValid()) return;
                            if (k === 'plan' && (!isClienteValid() || !isTarjetaValid())) return;
                            if (k === 'resumen' && (!isClienteValid() || !isTarjetaValid() || !isPlanValid())) return;
                            setActiveTab(k);
                        }}
                        className="mb-4 custom-tabs"
                    >
                        <Tab eventKey="cliente" title="1. Cliente">
                            <Row className="mb-3">
                                <Form.Group as={Col} controlId="formFirstName">
                                    <Form.Label className="fw-bold small">Nombre(s)</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="firstName" 
                                        placeholder="Nombre" 
                                        required 
                                        value={formData.firstName}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                                <Form.Group as={Col} controlId="formLastName">
                                    <Form.Label className="fw-bold small">Apellido(s)</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="lastName" 
                                        placeholder="Apellido Apellido" 
                                        required 
                                        value={formData.lastName}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Row>
                            <Form.Group className="mb-3" controlId="formEmail">
                                <Form.Label className="fw-bold small">Correo Electrónico</Form.Label>
                                <Form.Control 
                                    type="email" 
                                    name="email" 
                                    placeholder="Correo electrónico" 
                                    required 
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="formAddress">
                                <Form.Label className="fw-bold small">Dirección</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    name="address1" 
                                    placeholder="Calle y número exterior" 
                                    required 
                                    value={formData.address1}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                            <Row className="mb-3">
                                <Form.Group as={Col} controlId="formCity">
                                    <Form.Label className="fw-bold small">Ciudad</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="city" 
                                        placeholder="Ciudad" 
                                        required 
                                        value={formData.city}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                                <Form.Group as={Col} controlId="formCountry">
                                    <Form.Label className="fw-bold small">País</Form.Label>
                                    <Form.Select name="country" value={formData.country} onChange={handleChange}>
                                        <option value="MX">México (MX)</option>
                                        {/*<option value="US">Estados Unidos (US)</option>*/}
                                    </Form.Select>
                                </Form.Group>
                            </Row>
                            <div className="d-flex justify-content-end mt-4">
                                <Button 
                                    variant="primary" 
                                    onClick={() => setActiveTab('tarjeta')} 
                                    style={{ backgroundColor: '#0E9600', border: 'none' }}
                                    disabled={!isClienteValid()}
                                >
                                    Siguiente: Tarjeta <i className="bi bi-arrow-right ms-1"></i>
                                </Button>
                            </div>
                        </Tab>

                        <Tab eventKey="tarjeta" title="2. Tarjeta" disabled={!isClienteValid()}>
                            <Form.Group className="mb-3" controlId="formHolderName">
                                <Form.Label className="fw-bold small">Nombre en la Tarjeta</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    name="holderName" 
                                    placeholder="Nombre Apellido Apellido" 
                                    required 
                                    value={formData.holderName}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="formPan">
                                <Form.Label className="fw-bold small">
                                    Número de Tarjeta
                                    {renderBrandBadge()}
                                </Form.Label>
                                <Form.Control 
                                    type="text" 
                                    name="pan" 
                                    placeholder={getCardBrand(formData.pan) === 'amex' ? "0000 0000 0000 000000" : "0000 0000 0000 0000"} 
                                    maxLength={getCardBrand(formData.pan) === 'amex' ? "15" : "16"}
                                    required 
                                    value={formData.pan}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                            <Row className="mb-3">
                                <Form.Group as={Col} controlId="formExpMonth">
                                    <Form.Label className="fw-bold small">Mes Exp.</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="expMonth" 
                                        placeholder="MM" 
                                        maxLength="2"
                                        required 
                                        value={formData.expMonth}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                                <Form.Group as={Col} controlId="formExpYear">
                                    <Form.Label className="fw-bold small">Año Exp.</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="expYear" 
                                        placeholder="AAAA" 
                                        maxLength="4"
                                        required 
                                        value={formData.expYear}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                                {formData.initialCharge && (
                                    <Form.Group as={Col} controlId="formCvv">
                                        <Form.Label className="fw-bold small">
                                            CVV <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Control 
                                            type="password" 
                                            name="cvv" 
                                            placeholder={getCardBrand(formData.pan) === 'amex' ? "1234" : "123"} 
                                            maxLength={getCardBrand(formData.pan) === 'amex' ? "4" : "3"}
                                            required
                                            value={formData.cvv}
                                            onChange={handleChange}
                                        />
                                    </Form.Group>
                                )}
                            </Row>
                            <Form.Group className="mb-3" controlId="formInitialCharge">
                                <Form.Check 
                                    type="checkbox"
                                    name="initialCharge"
                                    label="¿Aplicar Cargo Inicial / Anticipo?"
                                    className="fw-bold small text-muted"
                                    checked={formData.initialCharge}
                                    onChange={handleChange}
                                />
                                {formData.initialCharge && (
                                    <small className="text-warning d-block mt-1" style={{ fontSize: '12px' }}>
                                        ⚠️ <strong>Nota:</strong> Este cobro se procesará inmediatamente de forma adicional e independiente al monto total del plan recurrente.
                                    </small>
                                )}
                            </Form.Group>
                            <div className="d-flex justify-content-between mt-4">
                                <Button variant="outline-secondary" onClick={() => setActiveTab('cliente')}>
                                    <i className="bi bi-arrow-left me-1"></i> Anterior
                                </Button>
                                <Button 
                                    variant="primary" 
                                    onClick={() => setActiveTab('plan')} 
                                    style={{ backgroundColor: '#0E9600', border: 'none' }}
                                    disabled={!isTarjetaValid()}
                                >
                                    Siguiente: Plan <i className="bi bi-arrow-right ms-1"></i>
                                </Button>
                            </div>
                        </Tab>

                        <Tab eventKey="plan" title="3. Plan Recurrente" disabled={!isClienteValid() || !isTarjetaValid()}>
                            <Row className="mb-3">
                                <Form.Group as={Col} controlId="formConcept">
                                    <Form.Label className="fw-bold small">Concepto del Plan</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="concept" 
                                        placeholder="Ej. Nombre producto o plan" 
                                        required 
                                        value={formData.concept}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Row>
                            <Row className="mb-3">
                                <Form.Group as={Col} controlId="formAmountPlan">
                                    <Form.Label className="fw-bold small">Monto Total del Plan</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="amount" 
                                        placeholder="Ej. 1,250.00" 
                                        required 
                                        value={formatWithCommas(formData.amount)}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                                <Form.Group as={Col} controlId="formInitialAmount">
                                    <Form.Label className="fw-bold small">Cargo Inicial / Anticipo</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="initialAmount" 
                                        placeholder={formData.initialCharge ? "Ej. 150.00" : "Habilita en paso 2"} 
                                        disabled={!formData.initialCharge}
                                        required={formData.initialCharge}
                                        value={formData.initialCharge ? formatWithCommas(formData.initialAmount) : '0'}
                                        onChange={handleChange}
                                    />
                                    {formData.initialCharge && (
                                        <Form.Text className="text-warning d-block mt-1" style={{ fontSize: '11px', fontWeight: 'bold' }}>
                                            ⚠️ Este cargo se cobrará al instante y es adicional/independiente al Monto Total del Plan.
                                        </Form.Text>
                                    )}
                                </Form.Group>
                                <Form.Group as={Col} controlId="formCurrencyPlan">
                                    <Form.Label className="fw-bold small">Moneda</Form.Label>
                                    <Form.Select name="currency" value={formData.currency} onChange={handleChange}>
                                        <option value="484">MXN (Pesos)</option>
                                        {/*<option value="840">USD (Dólares)</option>*/}
                                    </Form.Select>
                                </Form.Group>
                            </Row>
                            <Row className="mb-3">
                                <Form.Group as={Col} controlId="formPeriodicity">
                                    <Form.Label className="fw-bold small">Periodicidad</Form.Label>
                                    <Form.Select name="periodicity" value={formData.periodicity} onChange={handleChange}>
                                        {/*<option value="weekly">Semanal</option>*/}
                                        <option value="biweekly">Quincenal</option>
                                        <option value="monthly">Mensual</option>
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group as={Col} controlId="formChargeNumber">
                                    <Form.Label className="fw-bold small">Número de Cargos</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        name="chargeNumber" 
                                        min="1"
                                        required 
                                        value={formData.chargeNumber}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Row>
                            <Row className="mb-3">
                                <Form.Group as={Col} controlId="formChargeDay">
                                    <Form.Label className="fw-bold small">Día de Cargo</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        name="chargeDay" 
                                        min="1"
                                        max="31"
                                        required 
                                        value={formData.chargeDay}
                                        onChange={handleChange}
                                        disabled={formData.periodicity !== 'monthly'}
                                    />
                                </Form.Group>
                                <Form.Group as={Col} controlId="formStartDatePlan">
                                    <Form.Label className="fw-bold small">Fecha de Inicio</Form.Label>
                                    <Form.Control 
                                        type="date" 
                                        name="startDate" 
                                        required 
                                        value={formData.startDate}
                                        onChange={handleChange}
                                        disabled
                                    />
                                </Form.Group>
                            </Row>
                            
                            
                                <div className="d-flex justify-content-between mt-4">
                                    <Button variant="outline-secondary" onClick={() => setActiveTab('tarjeta')}>
                                        <i className="bi bi-arrow-left me-1"></i> Anterior
                                    </Button>

                                    <Button 
                                        variant="primary" 
                                        style={{ backgroundColor: '#0E9600', border: 'none' }}
                                        disabled={!isPlanValid()}
                                        onClick={() => setActiveTab('resumen')}
                                    >
                                        Siguiente: Desglose <i className="bi bi-arrow-right ms-1"></i>
                                    </Button>
                                </div>
                            </Tab>

                            <Tab eventKey="resumen" title="4. Desglose" disabled={!isClienteValid() || !isTarjetaValid() || !isPlanValid()}>
                                <div className="resumen-cobros p-3 border rounded mb-4" style={{ backgroundColor: '#F8F9FA', fontSize: '14px', fontFamily: 'Montserrat, sans-serif' }}>
                                    <Row className="mb-3">
                                        <Col md={6} className="mb-3 mb-md-0">
                                            <div className="mb-3">
                                                <span className="text-muted small fw-bold d-block mb-1">CONCEPTO</span>
                                                <span className="fw-bold d-block" style={{ color: '#3E3E3E' }}>{formData.concept}</span>
                                            </div>
                                            <div className="mb-3">
                                                <span className="text-muted small fw-bold d-block mb-1">CLIENTE</span>
                                                <span className="fw-bold d-block" style={{ color: '#3E3E3E' }}>{formData.firstName} {formData.lastName}</span>
                                                <small className="text-muted">{formData.email}</small>
                                            </div>
                                            <div>
                                                <span className="text-muted small fw-bold d-block mb-1">DIRECCIÓN</span>
                                                <span className="d-block text-truncate" style={{ color: '#3E3E3E' }}>{formData.address1}, {formData.city}, {formData.country}</span>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="mb-3">
                                                <span className="text-muted small fw-bold d-block mb-1">TARJETA</span>
                                                <span className="fw-bold d-block" style={{ color: '#3E3E3E' }}>
                                                    💳 **** **** **** {formData.pan.slice(-4)}
                                                    {getCardBrand(formData.pan) !== 'unknown' && ` (${getCardBrand(formData.pan).toUpperCase()})`}
                                                </span>
                                                <small className="text-muted">Titular: {formData.holderName}</small>
                                            </div>
                                            <div>
                                                <span className="text-muted small fw-bold d-block mb-1">CONFIGURACIÓN DEL PLAN</span>
                                                <span className="fw-bold d-block text-success">
                                                    Monto Total: ${formatWithCommas((parseFloat(formData.amount) || 0).toFixed(2))} {formData.currency === '484' ? 'MXN' : 'USD'}
                                                </span>
                                                {parseFloat(formData.initialAmount) > 0 && (
                                                     <>
                                                         <span className="fw-bold d-block text-success">
                                                             Cargo Inicial / Anticipo: ${formatWithCommas((parseFloat(formData.initialAmount) || 0).toFixed(2))} {formData.currency === '484' ? 'MXN' : 'USD'}
                                                         </span>
                                                         <small className="text-warning d-block mb-1 fw-bold" style={{ fontSize: '11px', lineHeight: '1.2' }}>
                                                             * Cobro inmediato y adicional al total de cargos del plan.
                                                         </small>
                                                     </>
                                                )}
                                                <small className="text-muted d-block mt-1">
                                                    {formData.chargeNumber} cargos de ${formatWithCommas(getRecurringChargeAmount())} | Cobro {formData.periodicity === 'monthly' ? `los días ${formData.chargeDay} de cada mes` : (formData.periodicity === 'weekly' ? 'semanal' : 'quincenal')}
                                                </small>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>

                                <h6 className="fw-bold mb-3 small text-muted" style={{ fontFamily: 'Montserrat, sans-serif' }}>CALENDARIO DE COBROS PROYECTADOS</h6>
                                <div className="table-responsive border rounded mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    <table className="table table-hover table-striped mb-0" style={{ fontSize: '13px', fontFamily: 'Montserrat, sans-serif' }}>
                                        <thead className="table-dark" style={{ position: 'sticky', top: 0 }}>
                                            <tr style={{ textAlign: 'center' }}>
                                                <th>Cobro #</th>
                                                <th>Fecha de Cobro</th>
                                                <th>Monto</th>
                                                <th>Estado</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {generatePaymentProjection().map((item) => (
                                                <tr key={item.index} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                    <td className="fw-bold">{item.index}</td>
                                                    <td>{moment(item.date).format('DD/MM/YYYY')}</td>
                                                    <td className="fw-bold text-success">${formatWithCommas(item.amount)} {formData.currency === '484' ? 'MXN' : 'USD'}</td>
                                                    <td>
                                                        <span className="badge bg-secondary px-3 py-2" style={{ borderRadius: '12px' }}>Proyectado</span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="d-flex justify-content-start mt-4">
                                    <Button variant="outline-secondary" onClick={() => setActiveTab('plan')}>
                                        <i className="bi bi-arrow-left me-1"></i> Anterior
                                    </Button>
                                </div>
                            </Tab>
                        </Tabs>
                    </Modal.Body>
                    <Modal.Footer className="border-0 p-4 pt-0">
                        <Button variant="secondary" onClick={handleHide} disabled={loading}>
                            Cancelar
                        </Button>
                        
                        {/*activeTab === 'plan' && (
                            <Button 
                                variant="primary" 
                                style={{ backgroundColor: '#0E9600', border: 'none' }}
                                disabled={!isPlanValid()}
                                onClick={() => setActiveTab('resumen')}
                            >
                                Siguiente: Desglose <i className="bi bi-arrow-right ms-1"></i>
                            </Button>
                    )}  */}

                        {activeTab === 'resumen' && (
                            <Button 
                                type="submit" 
                                style={{ backgroundColor: '#0E9600', border: 'none' }}
                                disabled={loading}
                            >
                                {loading ? 'Procesando...' : 'Confirmar y Activar Plan'}
                            </Button>
                        )}
                    </Modal.Footer>
            </Form>
        </Modal>
    );
};